import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  Container,
  InputGroup,
  Alert,
} from "@themesberg/react-bootstrap";
import { useNavigate } from "react-router-dom";
import BgImage from "../../assets/img/illustrations/signin.svg";
import API from "../../utils/api"; // ✅ Your axios instance
import { AuthContext } from "../../utils/AuthContext";
import { useTranslation } from "react-i18next";
import axios from "axios";
export default function Login() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false); // ✅ toggle between login / forgot password

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // ===== LOGIN HANDLER =====
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email.trim() || !password.trim()) {
      setError(t("loginTranslations.errors.fillAllFields"));
      return;
    }

    try {
      setLoading(true);
      const res = await login({ email, password });

      if (!res.success) {
        setError(res.message);
        return;
      }

      // redirect based on role
      if (res.user.role === "admin") {
        navigate("/admin/dashboard-overview");
      } else {
        navigate("/client/dashboard-overview");
      }
    } catch (err) {
      setError(t("loginTranslations.errors.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  // ===== FORGOT PASSWORD HANDLER =====
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    try {
      setLoading(true);

      // ✅ Direct axios call — no token included
      const res = await axios.post(`https://api-dashboard.nordmann-inkasso.de/api/auth/forgot-password`, { email });


      setSuccess(res.data.message);
      setEmail("");
    } catch (err) {
      setError(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };


  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <Row
            className="justify-content-center form-bg-image"
            style={{ backgroundImage: `url(${BgImage})` }}
          >
            <Col
              xs={12}
              className="d-flex align-items-center justify-content-center"
            >
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h3 className="mb-0">
                    {isForgotMode
                      ? "Forgot your password?"
                      : t("loginTranslations.heading")}
                  </h3>
                  {isForgotMode && (
                    <p className="text-muted mt-2">
                      Enter your email to receive a password reset link.
                    </p>
                  )}
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {/* ==== LOGIN FORM ==== */}
                {!isForgotMode && (
                  <Form className="mt-4" onSubmit={handleLogin}>
                    {/* Email */}
                    <Form.Group id="email" className="mb-4">
                      <Form.Label>
                        {t("loginTranslations.labels.email")}
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          autoFocus
                          required
                          type="email"
                          placeholder={t(
                            "loginTranslations.placeholders.email"
                          )}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>

                    {/* Password */}
                    <Form.Group id="password" className="mb-4">
                      <Form.Label>
                        {t("loginTranslations.labels.password")}
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="password"
                          placeholder={t(
                            "loginTranslations.placeholders.password"
                          )}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>

                    {/* Forgot password link */}
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none"
                        onClick={() => {
                          setIsForgotMode(true);
                          setError("");
                          setSuccess("");
                        }}
                      >
                        Forgot password?
                      </Button>
                    </div>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={loading}
                    >
                      {loading
                        ? t("loginTranslations.buttons.signingIn")
                        : t("loginTranslations.buttons.signIn")}
                    </Button>
                  </Form>
                )}

                {/* ==== FORGOT PASSWORD FORM ==== */}
                {isForgotMode && (
                  <Form className="mt-4" onSubmit={handleForgotPassword}>
                    <Form.Group id="email" className="mb-4">
                      <Form.Label>Email address</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send Reset Link"}
                    </Button>

                    <div className="text-center mt-3">
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none"
                        onClick={() => {
                          setIsForgotMode(false);
                          setError("");
                          setSuccess("");
                        }}
                      >
                        ← Back to Login
                      </Button>
                    </div>
                  </Form>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
}
